import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';
import { RunAction, RunActionReadable, RunStatus, RunStatusReadable } from './run';

/**
 * Append-only structured event log emitted by the worker as it processes
 * a run. Where `RunProgress` is the CURRENT snapshot the mobile cockpit
 * polls, `RunEvent` is the timeline of WHAT happened — the worker's
 * audit log so mobile + desktop UIs can render "Page 2 scraped: 23
 * jobs", "Filtered: not remote", "LLM eval: skip (salary too low)",
 * "Applying via Greenhouse...".
 *
 * Stored on DDB keyed by `USER#{userId}` / `EVENT#{runId}#{paddedSeq}`
 * with a 14-day TTL — event detail is for live monitoring and post-hoc
 * debug; long-term aggregates live on Run + Application.
 *
 * Seq is monotonically increasing within a run so clients can poll
 * `?sinceSeq=N` for incremental updates instead of paging by timestamp
 * (which races within the same millisecond).
 */

const RUN_EVENT_KIND_VALUES = [
  // Lifecycle
  'state_change',
  'page_scraped',

  // Per-job pipeline
  'job_seen',
  'job_pre_filter_skip',
  'job_extracted',
  'job_llm_evaluated',
  'job_route_decided',
  'application_started',
  'application_progress',
  'application_succeeded',
  'application_failed',
  'application_skipped',
  // #756 — fires every time the worker clicks LinkedIn's X on a card
  // to make it stop resurfacing (precheck-already-applied,
  // cross-run-dedup, prefilter-reject). Pairs with the
  // `jobsDismissed` counter on `RunCounters`. The pre-#756 world
  // emitted `application_skipped` for site 2 (duplicate-jobid-cross-run)
  // but NOTHING for sites 1 and 3, so dismiss volume was invisible to
  // the cloud event stream even though three code sites fired the X
  // click; this event is the first-class per-dismiss signal.
  'application_dismissed',
  // #399 — fires when the worker tried to dismiss a card (typically an
  // already-applied LinkedIn job) but both attempts at clicking the X
  // returned false. Surfaces to the mobile cockpit's needs-review queue
  // so the user can manually clear the card from their feed; without
  // this, the same already-applied card silently re-surfaces every
  // cycle, burning evaluation cost on each pass.
  'application_dismiss_failed',
  // #412 phase-5 — fires when LinkedIn's login-time reCAPTCHA escalates
  // to an image puzzle (or doesn't auto-resolve within the budget) and
  // the worker needs a human to solve it. Headless cycles can't solve
  // image puzzles programmatically; the desktop auto-relaunches in
  // headed mode and shows the captcha to the user. The cloud handler
  // routes this event to a mobile push so the user gets pinged on
  // their phone if they're away from the desktop.
  'captcha_required',

  // Forms / vision
  'form_recovery_attempt',
  'form_recovery_result',
  'form_learning_captured',

  // LinkedIn Easy Apply resume picker — see applicator/linkedin-resume.ts.
  // The worker emits `resume_resolved` on success regardless of branch so
  // the UI can show "Used 'Adam-Vella-2026.pdf'" or "Uploaded fresh". The
  // other two are warning-shaped: the user asked for a named resume that
  // LinkedIn no longer has, or the upload itself failed and the job was
  // abandoned without submitting.
  'resume_resolved',
  'resume_preferred_name_missing',
  'resume_upload_failed',

  // Errors / warnings — coarse-grained; debug traces stay on the device
  'warning',
  'error',

  // Free-form info — sparingly used; prefer typed kinds above
  'note',
] as const;
export const RunEventKind = z.enum(RUN_EVENT_KIND_VALUES);
export type RunEventKind = z.infer<typeof RunEventKind>;

/** Forward-compat read variant — see #482. */
export const RunEventKindReadable = z
  .enum([...RUN_EVENT_KIND_VALUES, 'unknown'] as const)
  .catch('unknown');
export type RunEventKindReadable = z.infer<typeof RunEventKindReadable>;

/**
 * Loose payload — different kinds carry different shape. Validation is
 * intentionally permissive so the worker can grow new event types
 * without a schema migration on every emit.
 *
 * Two variants:
 *  - `RunEventPayloadStrict` — used by the WRITE path
 *    (`AppendRunEventsRequest`). A misbehaving worker POSTing
 *    `payload.state: 'ROGUE'` gets a 400 rather than silently persisting
 *    'unknown' to DDB.
 *  - `RunEventPayload` — used by the READ path (`RunEvent`). Forward-
 *    compatible — an older mobile client parsing a diagnostic snapshot
 *    with a not-yet-bundled enum coerces to 'unknown' rather than
 *    dropping the whole event batch.
 */
const runEventPayloadShape = {
  title: z.string().optional(),
  company: z.string().optional(),
  linkedinJobId: z.string().optional(),
  url: z.string().optional(),
  /** Pre-filter reason, LLM evaluator reason, recovery reason. */
  reason: z.string().optional(),
  /** Route decision: 'easy-apply' | 'external' | 'save-workday' | 'save-unknown'. */
  routeType: z.string().optional(),
  /** ATS host for external applications. */
  atsHost: z.string().optional(),
  /** Counters snapshot at the moment of the event. */
  jobsOnPage: z.number().int().optional(),
  pageNum: z.number().int().optional(),
  /** Booleans + numbers used by various kinds. */
  fit: z.boolean().optional(),
  confidence: z.number().min(0).max(1).optional(),
  /** Vision / recovery: number of screenshots, number of strategies. */
  count: z.number().int().nonnegative().optional(),
  /** Free-form text — keep <500 chars. The worker truncates. */
  message: z.string().max(500).optional(),
  /** Open extension bucket for kinds we add later without redeploying
   *  the API; clients display unknown fields verbatim. */
  extra: z.record(z.string(), z.unknown()).optional(),
};

export const RunEventPayloadStrict = z
  .object({
    ...runEventPayloadShape,
    state: RunStatus.optional(),
    action: RunAction.optional(),
  })
  .strict();
export type RunEventPayloadStrict = z.infer<typeof RunEventPayloadStrict>;

const RunEventPayload = z
  .object({
    ...runEventPayloadShape,
    state: RunStatusReadable.optional(),
    action: RunActionReadable.optional(),
  })
  .strict();
export type RunEventPayload = z.infer<typeof RunEventPayload>;

export const RunEvent = z.object({
  userId: UserId,
  runId: z.string(),
  /** Strictly increasing within the run. Server is the source of truth —
   *  but we let the worker pre-assign per-batch seqs so a network retry
   *  doesn't duplicate. Server rejects gaps. */
  seq: z.number().int().nonnegative(),
  kind: RunEventKindReadable,
  /** Wall-clock the worker emitted the event. */
  ts: IsoTimestamp,
  payload: RunEventPayload,
  /** Epoch-seconds DDB TTL (~14d after emission). */
  ttl: z.number().int(),
});
export type RunEvent = z.infer<typeof RunEvent>;

/**
 * Batched append — worker flushes a buffer of structured events. Server
 * persists each one with the supplied seq; rejects any batch whose seqs
 * aren't strictly greater than the largest already-stored seq for the
 * run.
 */
export const AppendRunEventsRequest = z.object({
  events: z
    .array(
      z.object({
        seq: z.number().int().nonnegative(),
        // #482 — write-side: strict enums so a worker regression POSTing
        // `payload.state: 'ROGUE'` gets a 400 rather than silently
        // persisting 'unknown' to DDB (the "sink for bugs" concern
        // from the pre-merge review).
        kind: RunEventKind,
        ts: IsoTimestamp,
        payload: RunEventPayloadStrict,
      }),
    )
    .min(1)
    .max(100),
});
export type AppendRunEventsRequest = z.infer<typeof AppendRunEventsRequest>;

export const AppendRunEventsResponse = z.object({
  written: z.number().int().nonnegative(),
  /** Largest seq the server now holds — clients can use this to advance
   *  their local `lastSeenSeq` cursor without reading the events back. */
  highestSeq: z.number().int().nonnegative(),
});
export type AppendRunEventsResponse = z.infer<typeof AppendRunEventsResponse>;

export const ListRunEventsResponse = z.object({
  events: z.array(RunEvent),
  highestSeq: z.number().int().nonnegative(),
});
export type ListRunEventsResponse = z.infer<typeof ListRunEventsResponse>;
